import { useCallback, useRef, useState } from "react";
import useApi from "./useApi";
import { WorkflowCaseActionResult } from "../services/workflowService";

export type WorkflowCaseActionOutcome<T> = WorkflowCaseActionResult<T> | { kind: "failed" };

/**
 * Runs one Task / Pause / Cancel action on a real case at a time. A second click while one is in
 * flight is ignored (resolves null); a request that got no answer resolves `failed` after useApi
 * has raised its alert. Expected refusals (409 / 403 / 404 / 422) come back as data.
 */
const useWorkflowCaseAction = () => {
  const { executeApi } = useApi();
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const run = useCallback(
    async <TParams, TResult>(
      action: (params: TParams) => Promise<WorkflowCaseActionResult<TResult>>,
      params: TParams,
    ): Promise<WorkflowCaseActionOutcome<TResult> | null> => {
      if (busyRef.current) return null;
      busyRef.current = true;
      setBusy(true);
      try {
        return (await executeApi<TParams, WorkflowCaseActionResult<TResult>>(action, params)) ?? { kind: "failed" };
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [executeApi],
  );

  return { busy, run };
};

export default useWorkflowCaseAction;
